import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import logoAgs from '../../assets/logo-ags.png';

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  headerAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 56,
    width: '100%',
    backgroundColor: '#002542',
    zIndex: 0,
  },
  footerAccent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 56,
    width: '100%',
    backgroundColor: '#00a941',
    zIndex: 0,
  },
  contentWrapper: {
    position: 'relative',
    zIndex: 10,
    backgroundColor: '#ffffff',
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 24,
    width: '100%',
    alignItems: 'center',
  },
  logoContainer: {
    position: 'relative',
    zIndex: 10,
    backgroundColor: '#ffffff',
    alignItems: 'center',
    marginBottom: 12,
  },
  logo: {
    width: 200,
    height: 'auto',
  },
  giraHeader: {
    position: 'relative',
    zIndex: 10,
    fontSize: 14,
    fontWeight: 'bold',
    color: '#002542',
    textAlign: 'center',
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  idCard: {
    position: 'relative',
    zIndex: 10,
    borderWidth: 2,
    borderColor: '#002542',
    borderRadius: 6,
    padding: 20,
    width: '100%',
    maxWidth: 320,
    backgroundColor: '#fafafa',
  },
  credencialRow: {
    flexDirection: 'row',
    marginBottom: 10,
    alignItems: 'center',
  },
  credencialLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#002542',
    width: '32%',
  },
  credencialValue: {
    fontSize: 11,
    color: '#333333',
    width: '68%',
  },
  qrSection: {
    alignItems: 'center',
    marginTop: 18,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  qrCaption: {
    fontSize: 8,
    color: '#666666',
    marginTop: 6,
    textAlign: 'center',
  },
});

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Build the URL for the credential (QR points here). Opens on giras.zonasur.org.mx/credencial/:ref
 */
function buildCredencialUrl(baseUrl, referencia) {
  const base = (baseUrl || 'https://giras.zonasur.org.mx').replace(/\/$/, '');
  const ref = encodeURIComponent((referencia || '').trim());
  return ref ? `${base}/credencial/${ref}` : '';
}

const CredencialDocument = ({ jugador, club, gira, validez, qrDataUrl }) => (
  <Document>
    <Page size="LETTER" style={styles.page}>
      <View style={styles.headerAccent} />
      <View style={styles.footerAccent} />
      <View style={styles.contentWrapper}>
        <View style={styles.logoContainer}>
          <Image src={logoAgs} style={styles.logo} />
        </View>
        <Text style={styles.giraHeader}>{gira}</Text>
        <View style={styles.idCard}>
          <View style={styles.credencialRow}>
            <Text style={styles.credencialLabel}>Jugador</Text>
            <Text style={styles.credencialValue}>{jugador}</Text>
          </View>
          <View style={styles.credencialRow}>
            <Text style={styles.credencialLabel}>Club</Text>
            <Text style={styles.credencialValue}>{club}</Text>
          </View>
          <View style={styles.credencialRow}>
            <Text style={styles.credencialLabel}>Válido hasta</Text>
            <Text style={styles.credencialValue}>{validez}</Text>
          </View>
          <View style={styles.qrSection}>
            {qrDataUrl ? (
              <Image src={qrDataUrl} style={{ width: 140, height: 140 }} />
            ) : null}
            <Text style={styles.qrCaption}>Escanee el código para validar la informacion de la credencial</Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

/**
 * Generates a PDF credential with QR that points to the credential URL. Returns a Blob.
 * @param {Object} params - { jugador, club, gira, validez, referencia?, baseUrl? }
 */
const CredencialGira = async ({
  jugador,
  club,
  gira,
  validez,
  referencia = '',
  baseUrl = 'https://giras.zonasur.org.mx',
}) => {
  const jugadorClean = normalizeText(jugador);
  const clubClean = normalizeText(club);
  const giraClean = normalizeText(gira);
  const validezClean = normalizeText(validez);

  const credentialUrl = buildCredencialUrl(baseUrl, referencia);
  const qrContent = credentialUrl || `Jugador: ${jugadorClean}\nClub: ${clubClean}\nGira: ${giraClean}\nVálido hasta: ${validezClean}`;
  const qrDataUrl = await QRCode.toDataURL(qrContent, { width: 256, margin: 1 });

  const blob = await pdf(
    <CredencialDocument
      jugador={jugadorClean}
      club={clubClean}
      gira={giraClean}
      validez={validezClean}
      qrDataUrl={qrDataUrl}
    />
  ).toBlob();

  return blob;
};

export default CredencialGira;
